import { useRouter } from 'next/router';
import { useMemo } from 'react';

import {
  stripFlavor,
  isJobDetailsRoute,
  isJobRoute,
  isJobsRoute,
  isJobBoardRoute,
  isExternalJobRoute,
  isBlogRoute,
} from '@pflegia/util/common/path-utils';

/**
 * Hook that provides router paths with flavor segments stripped,
 * along with convenient route type checks.
 *
 * This is the primary hook for routing concerns in both app and lib code.
 * Use this when you need clean pathnames and/or route type checks.
 *
 * @example
 * ```tsx
 * const { pathname, asPath, isJobDetailsPage, isJobBoardPage } = useStrippedRouter();
 * // pathname: '/job-details/...' instead of '/pflegia/de/job-details/...'
 * // isJobDetailsPage: true/false
 * ```
 *
 * @returns Object containing stripped paths and route type flags
 */
export const useStrippedRouter = () => {
  const router = useRouter();

  const pathname = useMemo(() => stripFlavor(router.pathname), [router.pathname]);

  const asPath = useMemo(() => stripFlavor(router.asPath), [router.asPath]);

  const isJobDetailsPage = useMemo(() => isJobDetailsRoute(pathname), [pathname]);
  const isJobPage = useMemo(() => isJobRoute(pathname), [pathname]);
  const isJobsPage = useMemo(() => isJobsRoute(pathname), [pathname]);
  const isJobBoardPage = useMemo(() => isJobBoardRoute(pathname), [pathname]);
  const isBlogPage = useMemo(() => isBlogRoute(pathname), [pathname]);
  const isExternalJobPage = useMemo(() => isExternalJobRoute(pathname), [pathname]);

  return {
    pathname,
    asPath,
    isJobDetailsPage,
    isJobPage,
    isJobsPage,
    isJobBoardPage,
    isBlogPage,
    isExternalJobPage,
  };
};
